import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isChunkLoadError } from '@/lib/chunkError';

interface Props {
  children: ReactNode;
  /**
   * Changing this resets the boundary. The router passes the current path, so
   * navigating away from a broken page recovers instead of leaving the user
   * staring at the error with a nav bar that appears to do nothing.
   */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

/**
 * Catches render errors below it so one bad page cannot blank the whole app.
 *
 * React has no hook equivalent — `componentDidCatch` only exists on classes,
 * which is why this file is the only class component in the codebase.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Nothing ships errors off-box yet, so the console is the record. When a
    // reporter is added, this is its one call site.
    console.error('Unhandled render error', error, info.componentStack);
  }

  override componentDidUpdate(previous: Props): void {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private readonly retry = () => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const stale = isChunkLoadError(error);

    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="w-full max-w-md space-y-4 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-6 w-6 text-destructive" aria-hidden />
          </div>

          <div className="space-y-1.5">
            <h1 className="text-lg font-semibold">
              {stale ? 'A newer version is available' : 'Something went wrong'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {stale
                ? 'This page was updated while you had it open. Reload to pick up the new version.'
                : 'This page could not be displayed. Nothing you have saved is affected.'}
            </p>
          </div>

          {/* Only in development: a stack trace tells an employee nothing and
              can leak internals, but it is the first thing wanted locally. */}
          {import.meta.env.DEV && (
            <pre className="max-h-48 overflow-auto rounded-md bg-muted p-3 text-left text-xs text-muted-foreground">
              {error.stack ?? error.message}
            </pre>
          )}

          <div className="flex justify-center gap-2">
            {stale ? (
              <Button onClick={() => window.location.reload()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Reload
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={this.retry}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Try again
                </Button>
                <Button onClick={() => window.location.assign('/')}>Go to dashboard</Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}
